import { useState } from 'react';
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const MASK_CHAR = '•';

export interface KeyRowProps {
  label: string;
  value: string;
  copyAriaLabel: string;
  showLabel: string;
  hideLabel: string;
  copyKind: 'id' | 'secret';
  onCopy: (text: string, kind: 'id' | 'secret') => void;
  copied: boolean;
}

export function KeyRow({
  label,
  value,
  copyAriaLabel,
  showLabel,
  hideLabel,
  copyKind,
  onCopy,
  copied,
}: KeyRowProps) {
  const [visible, setVisible] = useState(false);
  const displayValue = visible
    ? value
    : MASK_CHAR.repeat(Math.min(value.length, 32));

  return (
    <Box sx={{ width: '100%' }}>
      <Typography
        variant="subtitle2"
        color="text.secondary"
        sx={{ mb: 0.5, display: 'block' }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          gap: 0,
          width: '100%',
          minWidth: 0,
        }}
      >
        <TextField
          fullWidth
          size="small"
          value={displayValue}
          inputProps={{
            readOnly: true,
            'aria-readonly': true,
            style: { cursor: 'default' },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title={visible ? hideLabel : showLabel}>
                  <IconButton
                    size="small"
                    onClick={() => setVisible((v) => !v)}
                    aria-label={visible ? hideLabel : showLabel}
                    edge="end"
                  >
                    {visible ? (
                      <VisibilityOffIcon fontSize="small" />
                    ) : (
                      <VisibilityIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              bgcolor: 'action.hover',
              '& fieldset': { borderColor: 'divider' },
              '&.Mui-focused fieldset': { borderWidth: 1 },
            },
          }}
        />
        <Tooltip title={copied ? 'Copié' : 'Copier'}>
          <IconButton
            size="small"
            onClick={() => onCopy(value, copyKind)}
            aria-label={copyAriaLabel}
            sx={{ ml: '16px' }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
